import React, { useState, useEffect } from 'react';
import { useParams } from 'wouter';
import './VMProfilePage.css';

interface VMProfileData {
  cpuCount?: number;
  cpus?: Array<{
    Name?: string;
    NumberOfCores?: number;
    NumberOfLogicalProcessors?: number;
    MaxClockSpeed?: number;
    CurrentClockSpeed?: number;
    L2CacheSize?: number;
    L3CacheSize?: number;
    ProcessorId?: string;
    Status?: string;
    ProcessorAffinity?: any;
  }>;
  cpuModel?: string; // for backward compatibility
  status?: string;
}

interface RouteParams {
  clientId?: string;
}

interface VMProfileModel {
  ServerFQDN: string;
  Environment: string;
  Location: string;
  Purpose: string;
  Credentials: {
    Username: string;
    Password: string;
    UseWindowsAuth: boolean;
  };
  CustomTags: Record<string, string>;
  DataCollectionMethod: string;
}

const VMProfilePage: React.FC = () => {
  const { clientId } = useParams<RouteParams>();
  const [localProfile, setLocalProfile] = useState<VMProfileData | null>(null);
  const [remoteProfile, setRemoteProfile] = useState<VMProfileData | null>(null);
  const [loadingLocal, setLoadingLocal] = useState<boolean>(false);
  const [loadingRemote, setLoadingRemote] = useState<boolean>(false);
  const [errorLocal, setErrorLocal] = useState<string | null>(null);
  const [errorRemote, setErrorRemote] = useState<string | null>(null);
  const [remoteFQDN, setRemoteFQDN] = useState<string>('');
  const [backendLog, setBackendLog] = useState<string | null>(null);
  const [testMessage, setTestMessage] = useState<string | null>(null);

  const fetchLocalVMProfile = async () => {
    setLoadingLocal(true);
    setErrorLocal(null);
    setBackendLog(null);
    try {
  // Use relative path so Vite dev server can proxy to the correct backend (port 9000)
  const response = await fetch(`/api/clients/${clientId || 'COSTCO'}/vm-profile/local`);
      if (!response.ok) {
        const text = await response.text();
        setBackendLog(text);
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const result: any = await response.json();

      // Normalize server response to the shape the UI expects.
      const normalize = (raw: any) => {
        // Determine cpu array from multiple possible shapes
        let cpusRaw: any[] = [];
        if (raw == null) cpusRaw = [];
        else if (Array.isArray(raw)) cpusRaw = raw;
        else if (Array.isArray(raw.cpus)) cpusRaw = raw.cpus;
        else if (Array.isArray(raw.cpuResources)) cpusRaw = raw.cpuResources;
        else if (raw.cpus && typeof raw.cpus === 'object') cpusRaw = [raw.cpus];
        else if (raw.cpuResources && typeof raw.cpuResources === 'object') cpusRaw = [raw.cpuResources];

        // Flatten any deeply nested arrays and keep only objects
        cpusRaw = (cpusRaw || []).flat(Infinity).filter((c: any) => c && typeof c === 'object');

        const cpus = cpusRaw.map((cpu: any) => ({
          Name: cpu.Name ?? cpu.name ?? 'N/A',
          NumberOfCores: cpu.NumberOfCores ?? cpu.Cores ?? cpu.cores ?? null,
          NumberOfLogicalProcessors: cpu.NumberOfLogicalProcessors ?? cpu.LogicalProcessors ?? cpu.LogicalProcessors ?? null,
          MaxClockSpeed: cpu.MaxClockSpeed ?? cpu.maxClockSpeed ?? null,
          CurrentClockSpeed: cpu.CurrentClockSpeed ?? cpu.currentClockSpeed ?? null,
          L2CacheSize: cpu.L2CacheSize ?? cpu.l2CacheSize ?? null,
          L3CacheSize: cpu.L3CacheSize ?? cpu.l3CacheSize ?? null,
          ProcessorId: cpu.ProcessorId ?? cpu.processorId ?? null,
          Status: cpu.Status ?? cpu.status ?? null,
          ProcessorAffinity: cpu.ProcessorAffinity ?? cpu.Affinity ?? cpu.affinity ?? null,
          // preserve any other keys if needed
          __raw: cpu
        }));

        return {
          cpuCount: raw?.cpuCount ?? cpus.length,
          cpus,
          status: raw?.status ?? raw?.state ?? (cpus.length > 0 ? 'ONLINE' : 'UNKNOWN')
        } as VMProfileData;
      };

      setLocalProfile(normalize(result));
    } catch (err) {
      setErrorLocal(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoadingLocal(false);
    }
  };

  const fetchRemoteVMProfile = async () => {
    setLoadingRemote(true);
    setErrorRemote(null);
    setBackendLog(null);
    try {
      const body = {
          ServerFQDN: remoteFQDN,
          Environment: "",
          Location: "",
          Purpose: "",
          Credentials: {
            Username: "",
            Password: "",
            UseWindowsAuth: true
          },
          CustomTags: {},
          DataCollectionMethod: "PowerShell"
      };
  // Use relative path so Vite dev server can proxy to the correct backend (port 9000)
  const response = await fetch(`/api/clients/${clientId || 'COSTCO'}/vm-profile/remote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (!response.ok) {
        const text = await response.text();
        setBackendLog(text);
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const result = await response.json();
      // Reuse the same normalization logic used for local responses
      const normalize = (raw: any) => {
        let cpusRaw: any[] = [];
        if (raw == null) cpusRaw = [];
        else if (Array.isArray(raw)) cpusRaw = raw;
        else if (Array.isArray(raw.cpus)) cpusRaw = raw.cpus;
        else if (Array.isArray(raw.cpuResources)) cpusRaw = raw.cpuResources;
        else if (raw.cpus && typeof raw.cpus === 'object') cpusRaw = [raw.cpus];
        else if (raw.cpuResources && typeof raw.cpuResources === 'object') cpusRaw = [raw.cpuResources];
        cpusRaw = (cpusRaw || []).flat(Infinity).filter((c: any) => c && typeof c === 'object');
        const cpus = cpusRaw.map((cpu: any) => ({
          Name: cpu.Name ?? cpu.name ?? 'N/A',
          NumberOfCores: cpu.NumberOfCores ?? cpu.Cores ?? cpu.cores ?? null,
          NumberOfLogicalProcessors: cpu.NumberOfLogicalProcessors ?? cpu.LogicalProcessors ?? cpu.LogicalProcessors ?? null,
          MaxClockSpeed: cpu.MaxClockSpeed ?? cpu.maxClockSpeed ?? null,
          CurrentClockSpeed: cpu.CurrentClockSpeed ?? cpu.currentClockSpeed ?? null,
          L2CacheSize: cpu.L2CacheSize ?? cpu.l2CacheSize ?? null,
          L3CacheSize: cpu.L3CacheSize ?? cpu.l3CacheSize ?? null,
          ProcessorId: cpu.ProcessorId ?? cpu.processorId ?? null,
          Status: cpu.Status ?? cpu.status ?? null,
          ProcessorAffinity: cpu.ProcessorAffinity ?? cpu.Affinity ?? cpu.affinity ?? null,
          __raw: cpu
        }));
        return {
          cpuCount: raw?.cpuCount ?? cpus.length,
          cpus,
          status: raw?.status ?? raw?.state ?? (cpus.length > 0 ? 'ONLINE' : 'UNKNOWN')
        } as VMProfileData;
      };

      setRemoteProfile(normalize(result.cpuResources || result));
    } catch (err) {
      setErrorRemote(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoadingRemote(false);
    }
  };

  useEffect(() => {
    fetchLocalVMProfile();
    // eslint-disable-next-line
  }, [clientId]);

  const [sidebarVisible, setSidebarVisible] = useState(false);

  return (
    <div className="vm-profile-dashboard-fullwidth">
      <div className="vm-profile-main" style={{ width: sidebarVisible ? 'calc(100% - 340px)' : '100%', maxWidth: '100vw', transition: 'width 0.2s' }}>
        <button style={{ position: 'absolute', top: 16, right: 16, zIndex: 10 }} onClick={() => setSidebarVisible(v => !v)}>
          {sidebarVisible ? 'Hide Logs' : 'Show Logs'}
        </button>
        <h1>VM Profile Dashboard</h1>
        <div className="vm-profile-columns">
          {/* Local VM Profile */}
          <div className="vm-profile-column">
            <h2>Local VM</h2>
            <button
              className="refresh-btn"
              onClick={() => {
                // show a quick test message and then run the real fetch
                setTestMessage('Refresh local invoked — running test call...');
                // clear the message after a short delay so it feels like a transient test
                setTimeout(() => setTestMessage(null), 3000);
                fetchLocalVMProfile();
              }}
              disabled={loadingLocal}
            >
              {loadingLocal ? 'Loading...' : 'Refresh Local'}
            </button>
            {testMessage && <div style={{ marginTop: 8, color: '#0366d6' }}>{testMessage}</div>}
            {errorLocal && (
              <div className="error-message">
                Error: {errorLocal}
              </div>
            )}
            {localProfile ? (
              <div className="vm-profile-card">
                <div className={`status ${localProfile.status === 'ONLINE' ? 'online' : 'offline'}`}> 
                  {localProfile.status}
                </div>
                <div className="info-row">
                  <span className="info-label">CPU Count:</span> {localProfile.cpuCount}
                </div>
                {Array.isArray(localProfile.cpus) && localProfile.cpus.length > 0 && (
                  <div className="cpu-details-list" style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(6, 1fr)',
                    gap: '2.5em 2.5em',
                    marginTop: '2em',
                    justifyItems: 'center',
                  }}>
                    {localProfile.cpus.map((cpu, idx) => (
                      <div className="cpu-details-card" key={idx} style={{
                        width: '270px',
                        maxWidth: '340px',
                        minWidth: '220px',
                        background: '#fff',
                        boxShadow: '0 2px 12px rgba(0,0,0,0.10)',
                        borderRadius: '12px',
                        padding: '1.5em',
                        border: '1px solid #e0e0e0',
                        margin: '0 0 0.5em 0'
                      }}>
                        <div style={{fontWeight: 600, marginBottom: '0.5em'}}>CPU #{idx + 1}</div>
                        <div><span className="info-label">Name:</span> {cpu.Name}</div>
                        <div><span className="info-label">Cores:</span> {cpu.NumberOfCores}</div>
                        <div><span className="info-label">Logical Processors:</span> {cpu.NumberOfLogicalProcessors}</div>
                        <div><span className="info-label">Max Clock Speed:</span> {cpu.MaxClockSpeed} MHz</div>
                        <div><span className="info-label">Current Clock Speed:</span> {cpu.CurrentClockSpeed} MHz</div>
                        <div><span className="info-label">L2 Cache Size:</span> {cpu.L2CacheSize} KB</div>
                        <div><span className="info-label">L3 Cache Size:</span> {cpu.L3CacheSize} KB</div>
                        <div><span className="info-label">Processor ID:</span> {cpu.ProcessorId}</div>
                        <div><span className="info-label">Status:</span> {cpu.Status}</div>
                        <div><span className="info-label">Affinity:</span> {cpu.ProcessorAffinity}</div>
                      </div>
                    ))}
                  </div>
                )}
                {/* Fallback for old cpuModel */}
                {(!localProfile.cpus || localProfile.cpus.length === 0) && localProfile.cpuModel && (
                  <div className="info-row">
                    <span className="info-label">CPU Model:</span> {localProfile.cpuModel}
                  </div>
                )}
              </div>
            ) : null}
            {!localProfile && !loadingLocal && !errorLocal && (
              <div className="no-vm-profile-data">No local VM profile data available.</div>
            )}
          </div>

          {/* Remote VM Profile */}
          <div className="vm-profile-column">
            <h2>Remote VM</h2>
            <div className="remote-vm-input-row">
              <input
                type="text"
                placeholder="Remote Server FQDN"
                value={remoteFQDN}
                onChange={e => setRemoteFQDN(e.target.value)}
                className="remote-vm-input"
              />
              <button className="refresh-btn" onClick={fetchRemoteVMProfile} disabled={loadingRemote || !remoteFQDN}>
                {loadingRemote ? 'Loading...' : 'Analyze Remote'}
              </button>
            </div>
            {errorRemote && (
              <div className="error-message">
                Error: {errorRemote}
              </div>
            )}
            {remoteProfile ? (
              <div className="vm-profile-card">
                <div className={`status ${remoteProfile.status === 'ONLINE' ? 'online' : 'offline'}`}>
                  {remoteProfile.status}
                </div>
                <div className="info-row">
                  <span className="info-label">CPU Count:</span> {remoteProfile.cpuCount}
                </div>
                {Array.isArray(remoteProfile.cpus) && remoteProfile.cpus.length > 0 && (
                  <div className="cpu-details-list">
                    {remoteProfile.cpus.map((cpu, idx) => (
                      <div className="cpu-details" key={idx} style={{marginBottom: '1em', padding: '0.5em', border: '1px solid #eee', borderRadius: '6px'}}>
                        <div><b>CPU #{idx + 1}</b></div>
                        <div><span className="info-label">Name:</span> {cpu.Name}</div>
                        <div><span className="info-label">Cores:</span> {cpu.NumberOfCores}</div>
                        <div><span className="info-label">Logical Processors:</span> {cpu.NumberOfLogicalProcessors}</div>
                        <div><span className="info-label">Max Clock Speed:</span> {cpu.MaxClockSpeed} MHz</div>
                        <div><span className="info-label">Current Clock Speed:</span> {cpu.CurrentClockSpeed} MHz</div>
                        <div><span className="info-label">L2 Cache Size:</span> {cpu.L2CacheSize} KB</div>
                        <div><span className="info-label">L3 Cache Size:</span> {cpu.L3CacheSize} KB</div>
                        <div><span className="info-label">Processor ID:</span> {cpu.ProcessorId}</div>
                        <div><span className="info-label">Status:</span> {cpu.Status}</div>
                      </div>
                    ))}
                  </div>
                )}
                {/* Fallback for old cpuModel */}
                {(!remoteProfile.cpus || remoteProfile.cpus.length === 0) && remoteProfile.cpuModel && (
                  <div className="info-row">
                    <span className="info-label">CPU Model:</span> {remoteProfile.cpuModel}
                  </div>
                )}
              </div>
            ) : null}
            {!remoteProfile && !loadingRemote && !errorRemote && (
              <div className="no-vm-profile-data">No remote VM profile data available.</div>
            )}
          </div>
        </div>
      </div>
      {sidebarVisible && (
        <div className="vm-profile-sidebar" style={{ width: 320, background: '#fafbfc', borderLeft: '1px solid #e0e0e0', padding: 16, position: 'fixed', top: 0, right: 0, height: '100vh', zIndex: 20, overflowY: 'auto' }}>
          <h3>Backend Logs / Error Details</h3>
          {backendLog ? (
            (() => {
              try {
                const parsed = backendLog ? JSON.parse(backendLog) : null;
                return (
                  <pre className="backend-log">
                    {JSON.stringify(parsed, null, 2)}
                  </pre>
                );
              } catch {
                return <pre className="backend-log">{backendLog}</pre>;
              }
            })()
          ) : (
            <div className="backend-log-empty">No backend logs to display.</div>
          )}
        </div>
      )}
    </div>
  );
};

export default VMProfilePage;